import { LESE_KNOTEN_KLASSEN } from "../lesen/lese-stile";
import { BEARBEITEN_ANSICHTEN } from "./bearbeiten-ansichten";
import { PersonenListe, SlashMenueVoll, type PersonenTreffer } from "./auswahl-listen";
import type { EditorAnfrage } from "./dialoge";

/*
 * Optionen des Bearbeiten-Pfads (Paket E-W2) für `useHornEditor` (`@horn/editor/kern`). Inhaltsstile = die des Lesepfads (Schrift
 * `text-base`, WYSIWYG zwischen Lesen und Bearbeiten, G-W3 A), nur Theme-Tokens und Standardklassen, ganze feste Ketten (R8), kein
 * `@horn/editor/theme.css`. Die Fläche behält die Mindesthöhe der Einfassung (kein Layoutsprung).
 */

/** Das bearbeitbare Element (ProseMirror-Wurzel): Mindesthöhe, Abstand zwischen den Blöcken wie im Lesepfad, kein eigener Fokusrahmen (den trägt der Rahmen). */
export const BEARBEITEN_FLAECHE_KLASSE = "relative min-h-56 p-3 sm:p-4 space-y-4 text-foreground focus:outline-hidden focus-visible:outline-0";

/** Die Tabelle im Editor: Hülle (Rahmen, Querlauf) und Tabelle, gleiche Ketten wie `LeseTabelle`. */
export const BEARBEITEN_TABELLE_KLASSEN = {
  huelle: "overflow-x-auto bg-layer border border-layer-line rounded-xl",
  tabelle: "min-w-full",
} as const;

/**
 * Alle Optionen, die `useHornEditor` für den Bearbeiten-Pfad braucht. So aufrufen:
 * `useHornEditor({ value: seiteAlsJson, onChange: () => {}, onJson, ...bearbeitenHuelleOptionen("Seiteninhalt", { suchePersonen, beiAnfrage, beiFormatverlust }) })`.
 * Der Editor ist bearbeitbar und registriert das vollständige Wiki-Schema mit den bearbeitbaren Darstellungen dieses Pakets und dem
 * vollen Slash-Menü; die Altfassungen der Knoten werden nicht eingehängt. Reine Daten und Bausteine; das Paket hängt nicht von
 * `@horn/editor` ab.
 *
 * - `suchePersonen`: bestehende, gesicherte Action der Seite (`@`-Auswahl der Personen); ohne sie gibt es kein `@`.
 * - `beiAnfrage`: Dialoge der Seite (Gitterwahl, Seitenwahl, Bildablage); ohne sie gibt es weder „Bild“ noch „Seite verlinken“.
 * - `beiFormatverlust`: einmaliger Hinweis der Seite, wenn beim Einfügen von HTML Formatierung verloren ging.
 */
export function bearbeitenHuelleOptionen(
  label: string,
  optionen: {
    suchePersonen?: (suche: string) => Promise<PersonenTreffer[]>;
    beiAnfrage?: (anfrage: EditorAnfrage) => void;
    beiFormatverlust?: () => void;
  } = {},
) {
  return {
    editorKlasse: BEARBEITEN_FLAECHE_KLASSE,
    editorAttribute: { "aria-label": label, "aria-multiline": "true" },
    knotenKlassen: LESE_KNOTEN_KLASSEN,
    wikiLinkSymbol: false,
    slash: { darstellung: SlashMenueVoll, zIndex: "70" },
    bearbeiten: {
      ansichten: BEARBEITEN_ANSICHTEN,
      tabelleKlassen: BEARBEITEN_TABELLE_KLASSEN,
      personenListe: PersonenListe,
      suchePersonen: optionen.suchePersonen,
      beiAnfrage: optionen.beiAnfrage,
      beiFormatverlust: optionen.beiFormatverlust,
    },
  } as const;
}
